import { useRef, useState } from "react";
import { ChevronDown, Lock, Camera, Phone, Users, Mail, Plus, Loader2 } from "lucide-react";
import { PriorityBadge } from "@/components/TaskBadges";
import { Avatar } from "@/components/shared/Avatar";
import { personColor } from "@/lib/taskCalendar";
import { deadlineLabel, doneWhen } from "./taskUtils";

// 360: Úkoly › Seznam na počítači jako tabulka (vzor Asana z laboratoře): skupiny podle Zobrazení (379), na konci
// Ke schválení a Hotové (sbalené), sloupce Název · Termín · Zakázka · Priorita · Kdo. S otevřeným detailem
// vpravo (compact) jen Název a Termín. Řádky nesou rowProps z useListNav (↑ ↓, najetí myší, mezerník).
const TYPE_ICON = { call: Phone, meeting: Users, email: Mail };
const TYPE_LABEL = { call: "Hovor", meeting: "Schůzka", email: "E-mail" };
const COLS = "grid-cols-[30px_minmax(0,1fr)_150px_minmax(120px,210px)_100px_92px]";
const COLS_COMPACT = "grid-cols-[30px_minmax(0,1fr)_124px]";
const GROUP_TONE = { review: "text-amber-600 dark:text-amber-400", done: "text-success" };
const DOW = ["ne", "po", "út", "st", "čt", "pá", "so"];
// Termín v tabulce krátce: „2 dny po termínu“, „dnes“, „zítra“, „pá 30. 10.“ (rok jen mimo letošek)
function dueText(t) {
  const dl = deadlineLabel(t);
  if (!dl || dl.text === "dnes" || dl.cls.includes("destructive")) return dl;
  const d = new Date(t.deadline), today = new Date(); today.setHours(0, 0, 0, 0);
  const day = new Date(d); day.setHours(0, 0, 0, 0);
  if (Math.round((day - today) / 86400000) === 1) return { ...dl, text: "zítra" };
  return { ...dl, text: `${DOW[d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}.${d.getFullYear() !== today.getFullYear() ? ` ${d.getFullYear()}` : ""}` };
}

export function ProjectPill({ id, name }) {
  if (!id) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="pp-pj inline-flex items-center gap-1.5 max-w-full h-6 px-2.5 rounded-full text-[12.5px] font-semibold" style={{ "--pc": personColor(id) }} title={name}>
      <i className="w-[7px] h-[7px] rounded-[2px] shrink-0" style={{ background: personColor(id) }} /><span className="truncate">{name}</span>
    </span>
  );
}

function StatusMark({ t }) {
  if (t.status === "Schváleno") return (
    <svg viewBox="0 0 20 20" className="w-5 h-5" aria-label="Hotovo"><circle cx="10" cy="10" r="9" fill="hsl(var(--success))" /><path d="m6.3 10.3 2.5 2.5 4.9-5.1" fill="none" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" /></svg>
  );
  return (   // čeká na schválení
    <svg viewBox="0 0 20 20" className="w-5 h-5" aria-label="Ke schválení"><circle cx="10" cy="10" r="8.2" fill="none" stroke="#f59e0b" strokeWidth="1.6" /><path d="M10 5a5 5 0 1 1-5 5h5z" fill="#f59e0b" /></svg>
  );
}

function CheckCircle({ t, can, onComplete }) {
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onComplete(t, e.currentTarget); }} disabled={!can} data-testid={`task-check-${t.id}`}
      title={can ? "Označit jako hotové" : "Dokončí řešitel"} aria-label={can ? "Označit jako hotové" : "Dokončí řešitel"}
      className={`group/ck w-[22px] h-[22px] rounded-full grid place-items-center ${can ? "text-muted-foreground hover:text-success" : "text-border cursor-default"}`}>
      <svg viewBox="0 0 20 20" className="w-5 h-5" aria-hidden="true"><circle cx="10" cy="10" r="8.2" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="m6.6 10.3 2.3 2.3 4.6-4.9" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className={`opacity-0 transition-opacity ${can ? "group-hover/ck:opacity-100" : ""}`} /></svg>
    </button>
  );
}

function Row({ t, i, compact, sel, open, rowProps, onOpen, onComplete, canComplete, isDelegated }) {
  const done = t.status === "Schváleno";
  const active = t.status === "Nový" || t.status === "V řešení";
  const dl = active ? dueText(t) : { text: doneWhen(t), cls: "text-muted-foreground" };
  const time = active && t.time_from && dl ? ` ${t.time_from}` : "";
  const TypeIcon = TYPE_ICON[t.type];
  const photos = t.completion?.photos?.length || t.photos?.length || 0;   // 363: i fotky přidané před dokončením
  const people = t.assignee_users || [];
  return (
    <div role="row" tabIndex={-1} {...rowProps(i)} onClick={() => onOpen(t.id)} data-testid={`task-row-${t.id}`}
      className={`relative grid ${compact ? COLS_COMPACT : COLS} items-center min-h-[44px] pl-4 pr-4 border-t border-border/60 cursor-pointer outline-none transition-colors
        ${open ? "bg-brand/[0.10]" : sel ? "bg-brand/[0.06]" : "hover:bg-secondary/50"}`}>
      {(sel || open) && <span className="absolute left-0 inset-y-0 w-[2px] bg-brand" aria-hidden="true" />}
      <span className="grid place-items-center">{active ? <CheckCircle t={t} can={canComplete(t)} onComplete={onComplete} /> : <StatusMark t={t} />}</span>
      <span className="min-w-0 flex items-center gap-2 pl-2.5">
        {TypeIcon && <TypeIcon size={15} className="text-muted-foreground shrink-0" aria-label={TYPE_LABEL[t.type]} />}
        <span className={`truncate text-[14px] font-semibold ${done ? "line-through text-muted-foreground" : ""}`}>{t.title}</span>
        {photos > 0 && <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-muted-foreground shrink-0" title="Fotky"><Camera size={13} />{photos}</span>}
        {!compact && isDelegated(t) && <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-bold uppercase shrink-0">Zadal jsem</span>}
        {t.private && <Lock size={13} className="text-muted-foreground shrink-0" aria-label="Soukromý" />}
      </span>
      <span className={`pl-2.5 text-[13px] tabular-nums whitespace-nowrap truncate ${dl?.cls || "text-muted-foreground"}`}>{dl ? `${dl.text}${time}` : ""}</span>
      {!compact && <span className="pl-2.5 min-w-0"><ProjectPill id={t.project_id} name={t.project_name} /></span>}
      {!compact && <span className="pl-2.5"><PriorityBadge value={t.priority || "Střední"} /></span>}
      {!compact && (
        <span className="pl-2.5 flex items-center" title={people.map((p) => p.name).join(", ")}>
          {people.slice(0, 3).map((p, k) => <Avatar key={p.id} name={p.name} photo={p.photo_url} size={24} className={`bg-brand/15 text-brand ring-2 ring-card ${k ? "-ml-1.5" : ""}`} />)}
          {people.length > 3 && <span className="ml-1 text-xs font-semibold text-muted-foreground">+{people.length - 3}</span>}
        </span>
      )}
    </div>
  );
}

// Rychlé přidání do skupiny (Enter uloží a pole zůstane pro další, Esc zavře).
function AddRow({ onAdd, testid }) {
  const [on, setOn] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const submit = async () => {
    const v = text.trim(); if (!v || busy) return;
    setBusy(true);
    try { await onAdd(v); setText(""); } finally { setBusy(false); ref.current?.focus(); }
  };
  if (!on) return (
    <button type="button" onClick={() => setOn(true)} data-testid={testid}
      className="w-full flex items-center gap-2.5 min-h-[40px] pl-[22px] pr-4 border-t border-border/60 text-[13.5px] text-muted-foreground hover:text-foreground hover:bg-secondary/40">
      <Plus size={15} /> Přidat úkol…
    </button>
  );
  return (
    <div className="flex items-center gap-2.5 min-h-[40px] pl-[22px] pr-4 border-t border-border/60">
      {busy ? <Loader2 size={15} className="animate-spin motion-reduce:animate-none text-muted-foreground" /> : <Plus size={15} className="text-muted-foreground" />}
      <input ref={ref} autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder="Název úkolu a Enter" data-testid={`${testid}-input`}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } if (e.key === "Escape") { e.stopPropagation(); setOn(false); setText(""); } }}
        onBlur={() => { if (!text.trim()) setOn(false); }}
        className="flex-1 min-w-0 h-8 bg-transparent outline-none text-[14px] font-semibold" />
    </div>
  );
}

// groups = taskGroups(); collapsed(key) / onToggle(key); index = pořadí řádku pro klávesnici (id → i).
export function TaskTable({ groups, compact, index, sel, openId, rowProps, onOpen, onComplete, canComplete, isDelegated,
  collapsed, onToggle, onAdd, more }) {
  return (
    <div className="bg-card border border-border rounded-[16px] overflow-clip" role="grid" aria-label="Úkoly" data-testid="task-table">
      <div className={`sticky top-0 z-10 grid ${compact ? COLS_COMPACT : COLS} items-center h-9 pl-4 pr-4 bg-card border-b border-border text-xs font-semibold text-muted-foreground`} role="row">
        <span />
        <span className="pl-2.5">Název úkolu</span><span className="pl-2.5">Termín</span>
        {!compact && <><span className="pl-2.5">Zakázka</span><span className="pl-2.5">Priorita</span><span className="pl-2.5">Kdo</span></>}
      </div>
      {groups.map((g, gi) => {
        const col = !g.flat && collapsed(g.key);   // 379: bez skupin = rozpracované bez lišty
        const addable = onAdd && !g.end && g.key !== "overdue";
        return (
          <section key={g.key} data-testid={`task-group-${g.key}`} className={gi ? "border-t border-border" : ""}>
            {!g.flat && (
              <button type="button" onClick={() => onToggle(g.key)} aria-expanded={!col} data-testid={`task-group-toggle-${g.key}`}
                className="w-full flex items-center gap-1.5 h-11 px-4 text-left hover:bg-secondary/30">
                <ChevronDown size={16} className={`text-muted-foreground transition-transform motion-reduce:transition-none ${col ? "-rotate-90" : ""}`} />
                <span className={`text-[14px] font-bold ${g.danger ? "text-destructive" : GROUP_TONE[g.key] || ""}`}>{g.label}</span>
                <span className="text-[12.5px] font-semibold text-muted-foreground tabular-nums">{g.count ?? g.items.length}</span>
              </button>
            )}
            {!col && g.items.map((t) => (
              <Row key={t.id} t={t} i={index.get(t.id)} compact={compact} sel={sel === index.get(t.id)} open={openId === t.id} rowProps={rowProps}
                onOpen={onOpen} onComplete={onComplete} canComplete={canComplete} isDelegated={isDelegated} />
            ))}
            {!col && addable && <AddRow onAdd={(title) => onAdd(g.key, title)} testid={`task-add-${g.key}`} />}
            {!col && g.key === "done" && more}
          </section>
        );
      })}
    </div>
  );
}
